import { useCallback, useState } from 'react';
import { TenantDetails } from './tenant-types';

/**
 * The fields both tenant requests carry. Create adds the owner credentials and the
 * identity patterns, edit adds Email; neither flow wants the other's, which is why
 * the two no longer share a component.
 */
export interface TenantCommonDetails {
  name: string;
  country: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  phoneNumber: string;
  notes: string;
  carrierPortalUrl: string;
  customerPortalUrl: string;
}

export const emptyCommonDetails: TenantCommonDetails = {
  name: '',
  country: '',
  streetAddress: '',
  city: '',
  state: '',
  postalCode: '',
  phoneNumber: '',
  notes: '',
  carrierPortalUrl: '',
  customerPortalUrl: ''
};

export function commonDetailsFromTenant(tenant: TenantDetails): TenantCommonDetails {
  return {
    name: tenant.name ?? '',
    country: tenant.country ?? '',
    streetAddress: tenant.streetAddress ?? '',
    city: tenant.city ?? '',
    state: tenant.state ?? '',
    postalCode: tenant.postalCode ?? '',
    phoneNumber: tenant.phoneNumber ?? '',
    notes: tenant.notes ?? '',
    carrierPortalUrl: tenant.carrierPortalUrl ?? '',
    customerPortalUrl: tenant.customerPortalUrl ?? ''
  };
}

/** A field only shows as invalid once the user has visited it. */
export type Touched<T> = Partial<Record<keyof T, boolean>>;

/**
 * Field plumbing shared by the create form and the edit form. Both hold a flat
 * record of non-nullable strings, so one hook serves both.
 */
export function useFieldState<T extends object>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [touched, setTouched] = useState<Touched<T>>({});

  // DevExtreme's onValueChanged hands back an event, and it puts null in `value`
  // when a field is cleared. Every field here is a non-nullable string, so the
  // `?? ''` is load-bearing.
  const setField = useCallback(
    (field: keyof T) => (e: { value?: string | null }) =>
      setValues(prev => ({ ...prev, [field]: e.value ?? '' } as T)),
    []
  );

  const markTouched = useCallback(
    (field: keyof T) => () => setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true })),
    []
  );

  const reset = useCallback((next: T) => {
    setValues(next);
    setTouched({});
  }, []);

  return { values, touched, setField, markTouched, reset };
}

export const isFilled = (value: string) => value.trim().length > 0;
export const isEmailAddress = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
export const MIN_OWNER_PASSWORD_LENGTH = 8;

/** Blank is allowed (no override); otherwise an absolute https URL, matching the API's rule. */
export const isOptionalHttpsUrl = (value: string) => {
  if (!isFilled(value)) return true;
  try {
    return new URL(value.trim()).protocol === 'https:';
  } catch {
    return false;
  }
};
